import { useState, useEffect } from 'react';
import './CustomerModal.css';

interface NewCustomerData {
  name: string;
  address: {
    street1: string;
    street2: string;
    city: string;
    state: string;
    postalCode: string;
  };
}

interface CustomerCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (customerData: NewCustomerData) => Promise<void>;
  title?: string;
  submitButtonText?: string;
}

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const CustomerCreateModal = ({
  isOpen,
  onClose,
  onSubmit,
  title = 'Request New Customer',
  submitButtonText = 'Create Customer'
}: CustomerCreateModalProps) => {
  const [customerData, setCustomerData] = useState<NewCustomerData>({
    name: '',
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      postalCode: ''
    }
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      resetModal();
    }
  }, [isOpen]);

  const resetModal = () => {
    setCustomerData({
      name: '',
      address: {
        street1: '',
        street2: '',
        city: '',
        state: '',
        postalCode: ''
      }
    });
    setError(null);
    setSuccess(false);
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!customerData.name || !customerData.address.street1 || 
        !customerData.address.city || !customerData.address.state || 
        !customerData.address.postalCode) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      await onSubmit(customerData);
      
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create customer');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {success ? (
            <div className="success-message">
              <div className="success-icon">✓</div>
              <p>Customer created successfully!</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="alert-error">
                  {error}
                </div>
              )}

              <div className="create-customer-form">
                <div className="form-group">
                  <label>
                    Customer Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter customer name"
                    value={customerData.name}
                    onChange={(e) => setCustomerData({
                      ...customerData,
                      name: e.target.value
                    })}
                  />
                </div>

                <h4 className="section-subtitle">Customer Address</h4>

                <div className="form-group">
                  <label>
                    Street Address <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="123 Main Street"
                    value={customerData.address.street1}
                    onChange={(e) => setCustomerData({
                      ...customerData,
                      address: { ...customerData.address, street1: e.target.value }
                    })}
                  />
                </div>

                <div className="form-group">
                  <label>Street Address 2</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Apt, Suite, Unit, etc. (optional)"
                    value={customerData.address.street2}
                    onChange={(e) => setCustomerData({
                      ...customerData,
                      address: { ...customerData.address, street2: e.target.value }
                    })}
                  />
                </div>

                <div className="address-row">
                  <div className="form-group">
                    <label>
                      City <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Akron"
                      value={customerData.address.city}
                      onChange={(e) => setCustomerData({
                        ...customerData,
                        address: { ...customerData.address, city: e.target.value }
                      })}
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      State <span className="required">*</span>
                    </label>
                    <select
                      className="form-input"
                      value={customerData.address.state}
                      onChange={(e) => setCustomerData({
                        ...customerData,
                        address: { ...customerData.address, state: e.target.value }
                      })}
                    >
                      <option value="">Select</option>
                      {US_STATES.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>
                      Postal Code <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="12345"
                      value={customerData.address.postalCode}
                      onChange={(e) => setCustomerData({
                        ...customerData,
                        address: { ...customerData.address, postalCode: e.target.value }
                      })}
                      pattern="[0-9]{5}"
                    />
                  </div>
                </div>

                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? 'Creating...' : submitButtonText}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
